/**
 * Kidify saved items and recently viewed.
 *
 * Both live in the shopper's own browser (localStorage), so they work
 * with no account and nothing is sent anywhere. Saved items are kept as
 * named lists (see `lists` below); assets/kidify-lists.js draws the lists
 * page, the "Saved to … · Change" toast and the list picker. Each entry keeps
 * the handle plus a snapshot (title, link, image, price, brand) taken
 * when it was saved or viewed; shelves refresh it from the store before
 * showing it and drop anything that has been taken off the site.
 *
 * Cards follow the product card template (CLAUDE.md): badges, price and
 * stock line come as HTML from /products/<handle>?view=kidify-card, which
 * renders the same snippets as every other card. miniCard() and
 * fetchCard() are shared with assets/kidify-lists.js; neither decides a
 * badge or price rule itself.
 *
 * Elements:
 *   <kidify-save>          heart button that saves or unsaves a product
 *   <kidify-save-later>    "Save for later" on a cart line: saves, then removes it
 *   <kidify-saved-count>   number of saved items, hidden at zero
 *   <kidify-recent-track>  on a product page, records the view
 *   <kidify-shelf>         a row of saved or recently viewed products
 *
 * localStorage can throw (private windows, blocked site data), so every
 * read and write is guarded; the page simply behaves as if empty.
 */

const KEYS = { saved: 'kidify:saved', recent: 'kidify:recent', lists: 'kidify:lists' };
const LIMITS = { saved: 60, recent: 12, lists: 20 };
const EVENT = 'kidify:shelf';
export const SAVED_EVENT = 'kidify:saved-added';

function readKey(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
}

function writeKey(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable: nothing to keep */
  }
}

/* ---------- Named lists ----------
 * Saved items are kept as named lists, Amazon style: { v, active, lists:
 * [{ id, name, at, items: [entry] }] } under kidify:lists. `active` is the
 * default list a heart saves to. The first time this runs it moves the old
 * single list (kidify:saved) into a default "Saved items" list, so nothing a
 * shopper saved before is lost. Every write also mirrors all saved items
 * back to kidify:saved, so a rollback to the old theme still finds them. */

const DEFAULT_NAME = 'Saved items';
const newId = () => `l${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

function readLists() {
  const state = readKey(KEYS.lists);
  if (state && Array.isArray(state.lists) && state.lists.length) {
    if (!state.lists.some((list) => list.id === state.active)) state.active = state.lists[0].id;
    return state;
  }
  const legacy = readKey(KEYS.saved);
  return {
    v: 1,
    active: 'default',
    lists: [{ id: 'default', name: DEFAULT_NAME, at: Date.now(), items: Array.isArray(legacy) ? legacy : [] }],
  };
}

function savedUnion(state) {
  const seen = new Set();
  return state.lists
    .flatMap((list) => list.items)
    .sort((a, b) => (b.at || 0) - (a.at || 0))
    .filter((entry) => entry?.handle && !seen.has(entry.handle) && seen.add(entry.handle));
}

function writeLists(state) {
  for (const list of state.lists) list.items = list.items.slice(0, LIMITS.saved);
  writeKey(KEYS.lists, state);
  writeKey(KEYS.saved, savedUnion(state).slice(0, LIMITS.saved));
  document.dispatchEvent(new CustomEvent(EVENT, { detail: { kind: 'saved' } }));
}

const findList = (state, id) => state.lists.find((list) => list.id === id);
const clean = (object) => Object.fromEntries(Object.entries(object).filter(([, value]) => value !== undefined && value !== null && value !== ''));

export const lists = {
  all: () => readLists(),
  get: (id) => findList(readLists(), id),
  activeId: () => readLists().active,
  listsWith: (handle) => readLists().lists.filter((list) => list.items.some((entry) => entry.handle === handle)),
  /** Any saved snapshot of a product, so it can be copied into another list. */
  entry(handle) {
    for (const list of readLists().lists) {
      const found = list.items.find((entry) => entry.handle === handle);
      if (found) return found;
    }
    return null;
  },
  create(name, items = []) {
    const state = readLists();
    if (state.lists.length >= LIMITS.lists) return null;
    const base = String(name || '').trim().slice(0, 50) || 'New list';
    let label = base;
    for (let n = 2; state.lists.some((list) => list.name.toLowerCase() === label.toLowerCase()); n++) label = `${base} (${n})`;
    const list = { id: newId(), name: label, at: Date.now(), items };
    state.lists.push(list);
    writeLists(state);
    return list;
  },
  rename(id, name) {
    const state = readLists();
    const list = findList(state, id);
    const label = String(name || '').trim().slice(0, 50);
    if (!list || !label) return;
    list.name = label;
    writeLists(state);
  },
  remove(id) {
    const state = readLists();
    if (state.lists.length <= 1) return;
    state.lists = state.lists.filter((list) => list.id !== id);
    if (state.active === id) state.active = state.lists[0].id;
    writeLists(state);
  },
  setActive(id) {
    const state = readLists();
    if (!findList(state, id)) return;
    state.active = id;
    writeLists(state);
  },
  /** Saving again keeps the shopper's note and the price first saved at,
   * which is what "Price dropped" compares against. */
  addTo(id, item) {
    if (!item?.handle) return;
    const state = readLists();
    const list = findList(state, id) || findList(state, state.active);
    const old = list.items.find((entry) => entry.handle === item.handle);
    const kept = old ? { note: old.note, savedCents: old.savedCents } : {};
    list.items = [{ ...item, ...clean(kept), at: Date.now() }, ...list.items.filter((entry) => entry.handle !== item.handle)];
    writeLists(state);
  },
  /** Change fields on one saved product (note, variant, saved price). */
  update(id, handle, patch) {
    lists.updateMany(id, [{ handle, patch }]);
  },
  /** Several products' fields in one write (one redraw). */
  updateMany(id, changes) {
    const state = readLists();
    const list = findList(state, id);
    if (!list || !changes.length) return;
    for (const { handle, patch } of changes) {
      const entry = list.items.find((item) => item.handle === handle);
      if (!entry) continue;
      for (const [key, value] of Object.entries(patch)) {
        if (value === undefined || value === null || value === '') delete entry[key];
        else entry[key] = value;
      }
    }
    writeLists(state);
  },
  removeFrom(id, handle) {
    const state = readLists();
    const list = findList(state, id);
    if (!list) return;
    list.items = list.items.filter((entry) => entry.handle !== handle);
    writeLists(state);
  },
  move(handle, fromId, toId) {
    const state = readLists();
    const from = findList(state, fromId);
    const to = findList(state, toId);
    const item = from?.items.find((entry) => entry.handle === handle);
    if (!item || !to || from === to) return;
    from.items = from.items.filter((entry) => entry.handle !== handle);
    to.items = [{ ...item, at: Date.now() }, ...to.items.filter((entry) => entry.handle !== handle)];
    writeLists(state);
  },
};

function read(kind) {
  if (kind === 'saved') return savedUnion(readLists());
  const list = readKey(KEYS[kind]);
  return Array.isArray(list) ? list : [];
}

function write(kind, list) {
  writeKey(KEYS[kind], list.slice(0, LIMITS[kind]));
  document.dispatchEvent(new CustomEvent(EVENT, { detail: { kind } }));
}

export const shelf = {
  list: read,
  has: (kind, handle) => read(kind).some((item) => item.handle === handle),
  /** Saved items go to the default list; recently viewed is a single list. */
  add(kind, item) {
    if (!item?.handle) return;
    if (kind === 'saved') return lists.addTo(lists.activeId(), item);
    const list = read(kind).filter((entry) => entry.handle !== item.handle);
    list.unshift({ ...item, at: Date.now() });
    write(kind, list);
  },
  /** Unsaving takes the product out of every list. */
  remove(kind, handle) {
    if (kind === 'saved') {
      const state = readLists();
      for (const list of state.lists) list.items = list.items.filter((entry) => entry.handle !== handle);
      return writeLists(state);
    }
    write(
      kind,
      read(kind).filter((entry) => entry.handle !== handle)
    );
  },
};

// Another tab changed a list: refresh this one.
window.addEventListener('storage', (event) => {
  if (event.key === KEYS.lists) document.dispatchEvent(new CustomEvent(EVENT, { detail: { kind: 'saved' } }));
  if (event.key === KEYS.recent) document.dispatchEvent(new CustomEvent(EVENT, { detail: { kind: 'recent' } }));
});

/**
 * The entry saved for a product. `savedCents` is the price when first
 * saved (the lists page flags a later drop); `variant` is the option the
 * shopper had picked, so the list can add that exact one to the cart.
 */
function itemFrom(element, variant) {
  const { handle, title, url, image, price, vendor, priceCents } = element.dataset;
  const cents = Number(priceCents);
  return clean({ handle, title, url, image, price, vendor, savedCents: Number.isFinite(cents) && priceCents !== '' ? cents : undefined, variant });
}

/** On the product page, the variant currently chosen in the buy form. */
function chosenVariant() {
  const field = document.querySelector('form[action*="/cart/add"] [name="id"]');
  const fromField = field?.value;
  const fromUrl = new URLSearchParams(window.location.search).get('variant');
  const id = fromField || fromUrl;
  return id && /^\d+$/.test(id) ? id : undefined;
}

/* ---------- Analytics ----------
 * A save is reported to Shopify's customer events (any custom pixel can pick
 * up "kidify_wishlist_add"), and to GA4 / Meta as their standard wishlist
 * events when those tags are on the page and the shopper's privacy choices
 * allow it. Nothing is sent when no tag is present. */

export function track(action, detail = {}) {
  try {
    window.Shopify?.analytics?.publish?.(`kidify_wishlist_${action}`, detail);
  } catch {
    /* customer events unavailable */
  }
  if (action !== 'add') return;
  const privacy = window.Shopify?.customerPrivacy;
  const analyticsOk = privacy?.analyticsProcessingAllowed ? privacy.analyticsProcessingAllowed() : true;
  const marketingOk = privacy?.marketingAllowed ? privacy.marketingAllowed() : true;
  const item = { item_id: detail.handle, item_name: detail.title, item_brand: detail.vendor };
  if (analyticsOk && typeof window.gtag === 'function') window.gtag('event', 'add_to_wishlist', { currency: 'SGD', items: [item] });
  if (marketingOk && typeof window.fbq === 'function') window.fbq('track', 'AddToWishlist', { content_ids: [detail.handle], content_name: detail.title });
}

/* ---------- Heart ---------- */

class KidifySave extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;
    this.button.addEventListener('click', this.#toggle);
    document.addEventListener(EVENT, this.#sync);
    this.#sync();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#toggle);
    document.removeEventListener(EVENT, this.#sync);
  }

  #toggle = (event) => {
    // Hearts sit on product cards, which are links.
    event.preventDefault();
    event.stopPropagation();
    const { handle } = this.dataset;
    if (shelf.has('saved', handle)) {
      shelf.remove('saved', handle);
    } else {
      const item = itemFrom(this, this.classList.contains('kidify-save--page') ? chosenVariant() : undefined);
      shelf.add('saved', item);
      track('add', { handle, title: item.title, vendor: item.vendor, source: 'heart' });
      document.dispatchEvent(new CustomEvent(SAVED_EVENT, { detail: { handle, undo: true } }));
      this.button.classList.remove('is-popped');
      void this.button.offsetWidth;
      this.button.classList.add('is-popped');
    }
  };

  #sync = () => {
    const saved = shelf.has('saved', this.dataset.handle);
    this.button.setAttribute('aria-pressed', String(saved));
    const label = saved ? this.dataset.labelSaved : this.dataset.labelSave;
    if (label) {
      this.button.setAttribute('aria-label', label);
      const text = this.querySelector('[data-save-text]');
      if (text) text.textContent = label;
    }
  };
}

/* ---------- Save for later (cart lines) ----------
 * Saves the line's product and variant to the default list, then presses
 * the line's own remove button, so the theme removes it from the cart and
 * redraws the drawer or page exactly as a normal removal would. */

class KidifySaveLater extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.#save);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#save);
  }

  #save = () => {
    const { handle, variant } = this.dataset;
    if (!handle) return;
    const item = itemFrom(this, variant);
    lists.addTo(lists.activeId(), item);
    track('add', { handle, title: item.title, vendor: item.vendor, source: 'cart' });
    this.button.disabled = true;
    const remove = this.closest('tr, .cart-items__table-row')?.querySelector('.cart-items__remove');
    remove?.click();
    document.dispatchEvent(new CustomEvent(SAVED_EVENT, { detail: { handle, undo: false, verb: 'Moved' } }));
  };
}

/* ---------- Saved count ---------- */

class KidifySavedCount extends HTMLElement {
  connectedCallback() {
    document.addEventListener(EVENT, this.#sync);
    this.#sync();
  }

  disconnectedCallback() {
    document.removeEventListener(EVENT, this.#sync);
  }

  #sync = () => {
    const count = shelf.list('saved').length;
    this.textContent = count > 99 ? '99+' : String(count);
    this.hidden = count === 0;
  };
}

/* ---------- Recently viewed tracker ---------- */

class KidifyRecentTrack extends HTMLElement {
  connectedCallback() {
    shelf.add('recent', itemFrom(this));
  }
}

/* ---------- Shelf ---------- */

export const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

export function sizedImage(src, width) {
  if (!src) return '';
  const url = src.startsWith('//') ? `https:${src}` : src;
  return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
}

export const money = (cents) => `$${(Number(cents) / 100).toFixed(2)}`;

/* ---------- Product card template ---------- */

const cardCache = new Map();

/** The card from the store: { card } , { gone: true } on a 404, or {} if offline. */
export function fetchCard(handle) {
  if (!cardCache.has(handle)) {
    cardCache.set(
      handle,
      fetch(`/products/${encodeURIComponent(handle)}?view=kidify-card`)
        .then((response) => {
          if (response.status === 404) return { gone: true };
          return response.ok ? response.json().then((card) => ({ card })) : {};
        })
        .catch(() => ({}))
    );
  }
  return cardCache.get(handle);
}

/**
 * The compact card link: photo with badges, brand, title, price, stock
 * line. The badge, price and stock HTML come from the store's snippets;
 * offline it falls back to the snapshot saved with the entry.
 */
export function miniCard({ card, entry }) {
  const url = card?.url ?? entry.url ?? `/products/${entry.handle}`;
  const image = card ? card.image && card.image : entry.image && sizedImage(entry.image, 360);
  const vendor = card?.vendor ?? entry.vendor;
  const title = card?.title ?? entry.title;
  const price = card ? card.priceHtml : entry.price ? `<span class="kidify-shelf__price">${escapeHtml(entry.price)}</span>` : '';
  return `<a class="kidify-shelf__card" href="${escapeHtml(url)}">
      <span class="kidify-shelf__media">
        ${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy" width="360" height="360">` : ''}
        ${card?.badgesHtml || ''}
      </span>
      ${vendor ? `<span class="kidify-shelf__vendor">${escapeHtml(vendor)}</span>` : ''}
      <span class="kidify-shelf__title">${escapeHtml(title)}</span>
      ${price}
      ${card?.stockHtml || ''}
    </a>`;
}

class KidifyShelf extends HTMLElement {
  #rendering = null;

  connectedCallback() {
    this.list = this.querySelector('[data-shelf-list]');
    this.empty = this.querySelector('[data-shelf-empty]');
    document.addEventListener(EVENT, this.#onChange);
    this.#render();
  }

  disconnectedCallback() {
    document.removeEventListener(EVENT, this.#onChange);
  }

  #onChange = (event) => {
    if (event.detail?.kind === this.dataset.kind) this.#render();
  };

  async #render() {
    const kind = this.dataset.kind;
    const limit = Number(this.dataset.limit) || 8;
    const exclude = this.dataset.exclude;
    const entries = shelf
      .list(kind)
      .filter((entry) => entry.handle && entry.handle !== exclude)
      .slice(0, limit);

    const token = Symbol('render');
    this.#rendering = token;

    if (!entries.length) {
      this.#show([]);
      return;
    }

    // Fresh card from the store. A 404 means the product has left the site.
    const fresh = await Promise.all(entries.map((entry) => fetchCard(entry.handle).then((result) => ({ ...result, entry }))));
    if (this.#rendering !== token) return;

    for (const result of fresh) if (result.gone) shelf.remove(kind, result.entry.handle);
    this.#show(fresh.filter((result) => !result.gone));
  }

  #show(results) {
    const cards = results.map(({ card, entry }) => {
      const heart =
        this.dataset.kind === 'saved'
          ? `<kidify-save class="kidify-save kidify-save--card" data-handle="${escapeHtml(entry.handle)}" data-label-save="Save" data-label-saved="Remove from saved">
               <button type="button" class="kidify-save__button" aria-pressed="true" aria-label="Remove from saved">${HEART}</button>
             </kidify-save>`
          : '';
      return `<li class="kidify-shelf__item">
          ${miniCard({ card, entry })}
          ${heart}
        </li>`;
    });

    if (this.list) this.list.innerHTML = cards.join('');
    if (this.empty) this.empty.hidden = cards.length > 0;
    // A shelf with nothing to show and no empty message hides entirely.
    this.hidden = cards.length === 0 && !this.empty;
  }
}

export const HEART =
  '<svg class="kidify-save__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 20.5s-7.5-4.6-9.3-9.2C1.4 8 3.4 4.5 6.9 4.5c2 0 3.5 1.1 4.3 2.4h1.6c.8-1.3 2.3-2.4 4.3-2.4 3.5 0 5.5 3.5 4.2 6.8-1.8 4.6-9.3 9.2-9.3 9.2Z" stroke-linejoin="round"/></svg>';

// For assets/kidify-lists.js, which runs after this file.
window.Kidify = Object.assign(window.Kidify || {}, { shelf, lists, escapeHtml, sizedImage, money, HEART, SAVED_EVENT, fetchCard, miniCard, track });

if (!customElements.get('kidify-save')) customElements.define('kidify-save', KidifySave);
if (!customElements.get('kidify-save-later')) customElements.define('kidify-save-later', KidifySaveLater);
if (!customElements.get('kidify-saved-count')) customElements.define('kidify-saved-count', KidifySavedCount);
if (!customElements.get('kidify-recent-track')) customElements.define('kidify-recent-track', KidifyRecentTrack);
if (!customElements.get('kidify-shelf')) customElements.define('kidify-shelf', KidifyShelf);
